import { computed, onMounted, reactive, Ref } from 'vue';
import { onBeforeUnmount } from '@vue/runtime-core';
import { throttle } from 'lodash';

export interface MaybeWebkitFullScreenApiHTMLVideoElement extends HTMLVideoElement {
    webkitEnterFullScreen: (options?: FullscreenOptions) => Promise<void> | undefined;
}

export default function useVideoPlayer(videoRef: Ref<MaybeWebkitFullScreenApiHTMLVideoElement>, progressBarRef: Ref<HTMLElement>) {
    let scrollPositionBeforeEnteringFullScreen = 0;
    const callbacksForTimeUpdate: any[] = [];
    const throttledTimeUpdateEventHandler = throttle(onTimeUpdate, 100);
    const state = reactive({
        durationRaw: 0,
        currentTime: '00:00',
        currentTimeRaw: 0,
        percentPlayedRaw: 0,
        percentPlayed: '0%',
        progressBarStyling: 'transform: translateX(-100%)',
        progressLabelStyling: 'transform: translateX(0%)',
        fullscreen: false,
        showTools: true,
        paused: true,
        muted: false,
    });

    function onTimeUpdate() {
        state.currentTimeRaw = Math.round(videoRef.value.currentTime);
        state.currentTime = prettifySeconds(state.currentTimeRaw);
        const durationRaw = Math.round(videoRef.value.duration);
        state.percentPlayedRaw = state.currentTimeRaw / durationRaw * 100;
        state.percentPlayed = `${Math.round(state.percentPlayedRaw)}%`;
        state.progressBarStyling = `transform: translateX(${state.percentPlayedRaw - 100}%)`;
        state.progressLabelStyling = `transform: translateX(${state.percentPlayedRaw}%)`;
        callbacksForTimeUpdate.forEach((callback) => callback());
    }

    const prettifySeconds = (totalSeconds: number) => {
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = Math.floor(totalSeconds - minutes * 60);
        const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
        const formattedSeconds = seconds < 10 ? `0${seconds}` : seconds;
        return `${formattedMinutes}:${formattedSeconds}`;
    };

    const setDuration = () => {
        state.durationRaw = Math.round(videoRef.value.duration ? videoRef.value.duration : 0);
        state.currentTime = prettifySeconds(state.durationRaw);
    };

    const togglePlaying = () => {
        if (videoRef.value.paused) {
            videoRef.value.play();
            state.paused = false;
        } else {
            videoRef.value.pause();
            state.paused = true;
        }
    };

    const toggleSound = () => {
        videoRef.value.muted = !videoRef.value.muted;
        state.muted = videoRef.value.muted;
    };

    const skipFiveSeconds = () => {
        videoRef.value.currentTime += 5;
    };

    const rewindFiveSeconds = () => {
        videoRef.value.currentTime -= 5;
    };

    const boostVolume = () => {
        videoRef.value.volume += 0.1;
    };

    const lowerVolume = () => {
        videoRef.value.volume -= 0.1;
    };

    const skipToClickedTime = (event: any) => {
        const percent = event.offsetX / progressBarRef.value.clientWidth * 100;
        const newTimeInSeconds = percent / 100 * videoRef.value.duration;
        videoRef.value.currentTime = videoRef.value.duration >= newTimeInSeconds ? newTimeInSeconds : videoRef.value.currentTime;
    };

    function onTimeProgress(callback: () => void) {
        callbacksForTimeUpdate.push(callback);
    }

    const onFullscreenChanged = () => {
        if (!document.fullscreenElement && scrollPositionBeforeEnteringFullScreen > 0) {
            document.documentElement.scrollTop = scrollPositionBeforeEnteringFullScreen;
            state.fullscreen = false;
        }
    };

    const enterFullScreen = () => {
        if (state.fullscreen === false) {
            scrollPositionBeforeEnteringFullScreen = document.body.scrollTop;
        }

        if (videoRef.value.requestFullscreen) {
            videoRef.value.requestFullscreen();
            state.fullscreen = true;
        } else if (videoRef.value.webkitEnterFullScreen) {
            videoRef.value.webkitEnterFullScreen();
            state.fullscreen = true;
        }
    };

    const fadeInTools = () => {
        state.showTools = true;
    };

    const fadeOutTools = () => {
        state.showTools = false;
    };

    onMounted(() => {
        videoRef.value.addEventListener('timeupdate', throttledTimeUpdateEventHandler);
        videoRef.value.addEventListener('fullscreenchange', onFullscreenChanged);
        videoRef.value.addEventListener('loadedmetadata', setDuration);
    });

    onBeforeUnmount(() => {
        videoRef.value.removeEventListener('timeupdate', throttledTimeUpdateEventHandler);
        videoRef.value.removeEventListener('fullscreenchange', onFullscreenChanged);
        videoRef.value.removeEventListener('loadedmetadata', setDuration);
    });

    return {
        enterFullScreen,
        prettifySeconds,
        setDuration,
        fadeOutTools,
        fadeInTools,
        rewindFiveSeconds,
        skipFiveSeconds,
        skipToClickedTime,
        togglePlaying,
        toggleSound,
        lowerVolume,
        boostVolume,
        onTimeProgress,
        currentTime: computed(() => state.currentTime),
        currentTimeRaw: computed(() => state.currentTimeRaw),
        durationRaw: computed(() => state.durationRaw),
        progressBarStyling: computed(() => state.progressBarStyling),
        progressLabelStyling: computed(() => state.progressLabelStyling),
        showTools: computed(() => state.showTools),
        percentPlayed: computed(() => state.percentPlayed),
        percentPlayedRaw: computed(() => state.percentPlayedRaw),
        paused: computed(() => state.paused),
        muted: computed(() => state.muted),
        fullscreen: computed(() => state.fullscreen),
    };
}
